import { useEffect, useState } from "react";
import { solesStrACent } from "@huayruro/shared";
import { solesCent } from "../lib/money";
import type { MetodoPago } from "../lib/tipos";

type Props = {
  totalCent: number;
  /** Lo que se está cobrando. Con «Cobrar» fijo abajo se puede cobrar sin mirar la venta (O-12). */
  items?: { nombre: string; cantidad: number; requiere_receta?: boolean }[];
  /** `recetasVistas[i]` = se marcó «Vi la receta» en el ítem i (solo puede ser true si el ítem pide receta). */
  onConfirmar: (metodo: MetodoPago, efectivoRecibidoCent: number | null, recetasVistas: boolean[]) => Promise<void>;
  onCancelar: () => void;
};

// La botica cobra en efectivo, Yape o Plin: esos tres van arriba y grandes. Los otros tres, abajo y
// más chicos, bajo el rótulo «Otro medio» (tabla 4.2 del tramo 02).
const PRINCIPALES: { value: MetodoPago; label: string; icon: string }[] = [
  { value: "efectivo", label: "Efectivo", icon: "💵" },
  { value: "yape", label: "Yape", icon: "📱" },
  { value: "plin", label: "Plin", icon: "📲" },
];
const OTROS: { value: MetodoPago; label: string; icon: string }[] = [
  { value: "tarjeta", label: "Tarjeta", icon: "💳" },
  { value: "transferencia", label: "Transferencia", icon: "🏦" },
  { value: "otro", label: "Otro", icon: "•" },
];

// Montos rápidos del efectivo: los billetes con que se paga en el mostrador.
const BILLETES = [10, 20, 50, 100];

// Parseo tolerante de soles → céntimos (vacío o inválido = 0).
function aCent(s: string): number {
  const limpio = s.trim().replace(",", ".");
  if (!/^\d+(\.\d{1,2})?$/.test(limpio)) return 0;
  try {
    return solesStrACent(limpio);
  } catch {
    return 0;
  }
}

const aSoles = (cent: number) => (cent / 100).toFixed(2);

export function CobrarModal({ totalCent, items = [], onConfirmar, onCancelar }: Props) {
  const [metodo, setMetodo] = useState<MetodoPago>("efectivo");
  const [recibidoStr, setRecibidoStr] = useState("");
  const [submitting, setSubmitting] = useState(false);
  // Freno de receta (26-oct-2026): una casilla por ítem que pide receta. Sin todas marcadas no se cobra;
  // el servidor vuelve a mirarlo y rechaza la venta si falta alguna.
  const [vistas, setVistas] = useState<Set<number>>(new Set());
  const conReceta = items.map((it, i) => ({ ...it, i })).filter((it) => it.requiere_receta);
  const faltaReceta = conReceta.some((it) => !vistas.has(it.i));
  const marcar = (i: number, si: boolean) =>
    setVistas((prev) => {
      const n = new Set(prev);
      if (si) n.add(i);
      else n.delete(i);
      return n;
    });

  useEffect(() => {
    function handleEsc(e: KeyboardEvent) {
      if (e.key === "Escape") onCancelar();
    }
    window.addEventListener("keydown", handleEsc);
    return () => window.removeEventListener("keydown", handleEsc);
  }, [onCancelar]);

  const recibidoCent = aCent(recibidoStr);
  const vueltoCent = recibidoCent - totalCent;
  const efectivoInsuficiente = metodo === "efectivo" && recibidoStr.trim() !== "" && recibidoCent < totalCent;
  const unidades = items.reduce((n, it) => n + it.cantidad, 0);

  async function handleSubmit() {
    setSubmitting(true);
    try {
      await onConfirmar(
        metodo,
        metodo === "efectivo" ? (recibidoStr.trim() ? recibidoCent : null) : null,
        items.map((it, i) => !!it.requiere_receta && vistas.has(i)),
      );
    } finally {
      setSubmitting(false);
    }
  }

  const botonMetodo = (m: { value: MetodoPago; label: string; icon: string }, grande: boolean) => (
    <button
      key={m.value}
      type="button"
      onClick={() => setMetodo(m.value)}
      aria-pressed={metodo === m.value}
      className={`rounded-[9px] border text-sm transition ${grande ? "min-h-[64px] p-2" : "min-h-[44px] flex-auto whitespace-nowrap px-3 py-1.5"} ${
        metodo === m.value ? "border-ok bg-ok-soft text-ok font-semibold" : "border-line-input text-ink hover:bg-hover-btn"
      }`}
    >
      <div className={grande ? "text-xl mb-0.5" : "inline mr-1"}>{m.icon}</div>
      <div className={grande ? "" : "inline"}>{m.label}</div>
    </button>
  );

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-ink-surface/40 backdrop-blur p-4">
      <div className="w-full max-w-md max-h-[calc(100dvh-32px)] overflow-y-auto bg-card border border-line rounded-[14px] p-5 sm:p-6 shadow-[0_10px_30px_rgba(36,29,26,0.25)]">
        <h2 className="text-2xl font-bold text-ink tabular-nums">Cobrar {solesCent(totalCent)}</h2>
        {items.length > 0 && (
          <p className="mt-1 text-[12.5px] leading-snug text-ink-2">
            {unidades} {unidades === 1 ? "producto" : "productos"}:{" "}
            {items
              .slice(0, 4)
              .map((it) => (it.cantidad > 1 ? `${it.nombre} ×${it.cantidad}` : it.nombre))
              .join(" · ")}
            {items.length > 4 ? ` y ${items.length - 4} más` : ""}
          </p>
        )}

        {conReceta.length > 0 && (
          <fieldset className="mt-4 rounded-[11px] border border-warn bg-warn-soft p-3" aria-label="Pide receta">
            <legend className="px-1 text-[13px] font-bold text-ink-strong">⚠ Pide receta</legend>
            <p className="mb-2 text-[12.5px] leading-snug text-ink-2">Pídela y mírala antes de cobrar. Si no la tiene, quítalo de la venta.</p>
            <div className="flex flex-col gap-1.5">
              {conReceta.map((it) => (
                <label key={it.i} className="flex min-h-[44px] cursor-pointer items-center gap-3 rounded-[9px] bg-card px-3 py-2 text-[14px] text-ink">
                  <input
                    type="checkbox"
                    className="h-5 w-5 flex-none accent-ok"
                    checked={vistas.has(it.i)}
                    onChange={(e) => marcar(it.i, e.target.checked)}
                  />
                  <span>
                    Vi la receta · <strong className="font-semibold">{it.nombre}</strong>
                  </span>
                </label>
              ))}
            </div>
          </fieldset>
        )}

        <div className="mt-4">
          <label className="block text-sm mb-2 text-ink-2">Método de pago</label>
          <div className="grid grid-cols-3 gap-2">{PRINCIPALES.map((m) => botonMetodo(m, true))}</div>
          {/* A la vista y más chicos, no escondidos detrás de un toque: «Otro medio» no puede esconder
              «Tarjeta» (objeción O-18 del tramo 02). */}
          <p className="mt-3 mb-1.5 text-[12px] text-ink-2">Otro medio</p>
          {/* Cada uno del ancho de su nombre (tramo 04): en 3 columnas iguales, a 390 px «🏦 Transferencia»
              no entraba en sus ~100 px, se salía del botón y pisaba al de «Otro». Si no entran en una fila,
              bajan a la siguiente; nunca se cortan. */}
          <div className="flex flex-wrap gap-2">{OTROS.map((m) => botonMetodo(m, false))}</div>
        </div>

        {metodo === "efectivo" && (
          <div className="mt-4">
            <label htmlFor="recibido" className="block text-sm mb-1 text-ink-2">
              Efectivo recibido (opcional)
            </label>
            <input
              id="recibido"
              type="text"
              inputMode="decimal"
              autoFocus
              value={recibidoStr}
              onChange={(e) => setRecibidoStr(e.target.value)}
              className="w-full min-h-[44px] px-3 py-2 rounded-[9px] bg-field border border-line-input focus:border-ok outline-none text-lg font-mono text-ink tabular-nums"
              placeholder="0.00"
            />
            <div className="mt-2 grid grid-cols-5 gap-1.5">
              <button
                type="button"
                onClick={() => setRecibidoStr(aSoles(totalCent))}
                className="min-h-[44px] rounded-[9px] border border-line-input text-[13px] font-semibold text-ink-emph hover:bg-hover-btn"
              >
                Exacto
              </button>
              {BILLETES.map((b) => (
                <button
                  key={b}
                  type="button"
                  onClick={() => setRecibidoStr(aSoles(b * 100))}
                  className="min-h-[44px] rounded-[9px] border border-line-input text-[13px] font-semibold tabular-nums text-ink-emph hover:bg-hover-btn"
                >
                  {b}
                </button>
              ))}
            </div>
            {recibidoCent > 0 && (
              // El vuelto es el número que se dice en voz alta: va grande.
              <p className={`mt-3 text-[22px] font-bold font-mono tabular-nums ${vueltoCent >= 0 ? "text-ok" : "text-accent-ink"}`}>
                {vueltoCent >= 0 ? `Vuelto: ${solesCent(vueltoCent)}` : `Falta: ${solesCent(-vueltoCent)}`}
              </p>
            )}
          </div>
        )}

        {/* Tramo 04: a 360 px las dos mitades iguales partían «Confirmar e imprimir» en dos renglones.
            «Cancelar» va del ancho de su texto (el «(Esc)» es de teclado: bajo 640 px no se muestra) y la
            acción principal toma el resto, en una línea. */}
        <div className="mt-6 flex gap-3">
          <button
            onClick={onCancelar}
            disabled={submitting}
            className="flex-none sm:flex-1 min-h-[44px] px-4 py-2.5 rounded-[9px] bg-card border border-line-input text-ink-emph hover:bg-hover-btn text-sm disabled:opacity-50"
          >
            Cancelar<span className="max-sm:hidden"> (Esc)</span>
          </button>
          <button
            onClick={() => void handleSubmit()}
            disabled={submitting || efectivoInsuficiente || faltaReceta}
            // `bg-ok` por contraste (blanco sobre ok-strong = 3,44:1 < 4,5:1). Ver Carrito.tsx.
            className="flex-1 min-h-[44px] whitespace-nowrap px-3 py-2.5 rounded-[9px] bg-ok hover:opacity-90 text-white font-semibold disabled:opacity-30"
          >
            {submitting ? "Procesando..." : faltaReceta ? "Marca la receta" : "Confirmar e imprimir"}
          </button>
        </div>
      </div>
    </div>
  );
}
